/**
 * NeoHospFin API Service — PostgreSQL Backend
 */

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// Session is rehydrated from localStorage on module load so a page
// refresh keeps the user signed in. Cleared via logout() on sign-out.
const STORAGE_KEY = "fintrack.session";
const _stored = (() => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); }
  catch { return null; }
})();
let currentUserId = (_stored && _stored.id) || "";
let currentUserRole = (_stored && _stored.role) || "";

export const setCurrentUser = (id, role) => { currentUserId = id; currentUserRole = role; };
export const getCurrentUserId = () => currentUserId;
export const getCurrentSession = () => {
  if (!currentUserId) return null;
  return { id: currentUserId, role: currentUserRole,
           name: (_stored && _stored.name) || "", email: (_stored && _stored.email) || "" };
};

export const login = async (email, password) => {
  // Direct fetch — login can't use apiCall (no headers yet).
  const res = await fetch(`${API_BASE}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Login failed" }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const user = await res.json();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  setCurrentUser(user.id, user.role);
  return user;
};

export const logout = () => {
  localStorage.removeItem(STORAGE_KEY);
  setCurrentUser("", "");
};

export const changeMyPassword = (current_password, new_password) =>
  apiCall("/me/password", {
    method: "PUT",
    body: JSON.stringify({ current_password, new_password }),
  });

const headers = () => ({
  "Content-Type": "application/json",
  "X-User-Id": currentUserId,
  "X-User-Role": currentUserRole,
});

async function apiCall(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, { headers: headers(), ...options });
  if (!res.ok) {
    // Auth failure — clear the cached session so the app falls back to
    // the login screen on the next render. A custom event lets App.jsx
    // react without coupling api.js to React state.
    if (res.status === 401) {
      logout();
      try { window.dispatchEvent(new CustomEvent("fintrack:auth-expired")); } catch {}
    }
    const err = await res.json().catch(() => ({ error: "Request failed" }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

// Dashboard
export const getDashboard = () => apiCall("/dashboard");

// Projects
//   light=true skips full P&L synthesis. Use for pages (Settings, mapping)
//   that only need basic fields (name, status, actual_year, vertical, etc.).
export const getProjects = (opts = {}) => {
  const qs = opts.light ? "?light=true" : "";
  return apiCall(`/projects${qs}`);
};
export const getProject = (id) => apiCall(`/projects/${id}`);
export const createProject = (data) => apiCall("/projects", { method: "POST", body: JSON.stringify(data) });
export const updateProject = (id, data) => apiCall(`/projects/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteProject = (id) => apiCall(`/projects/${id}`, { method: "DELETE" });
export const changeActualYear = (id, yr) => apiCall(`/projects/${id}/change-year`, { method: "POST", body: JSON.stringify({ actual_year: yr }) });
export const getProjectYearArchives = (id) => apiCall(`/projects/${id}/year-archives`);
export const addProjectHistoricalYear = (id, year) =>
  apiCall(`/projects/${id}/historical-year`, { method: "POST", body: JSON.stringify({ year }) });
export const removeProjectHistoricalYear = (id, year) =>
  apiCall(`/projects/${id}/historical-year/${year}`, { method: "DELETE" });

// Variables
export const addVariable = (pid, data) => apiCall(`/projects/${pid}/variables`, { method: "POST", body: JSON.stringify(data) });
export const updateVariable = (vid, data) => apiCall(`/variables/${vid}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteVariable = (vid) => apiCall(`/variables/${vid}`, { method: "DELETE" });
export const updateVariableValues = (vid, vals) => apiCall(`/variables/${vid}/values`, { method: "PUT", body: JSON.stringify(vals) });

// Companies
export const getCompanies = () => apiCall("/companies");
export const createCompany = (data) => apiCall("/companies", { method: "POST", body: JSON.stringify(data) });
export const updateCompany = (id, data) => apiCall(`/companies/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteCompany = (id) => apiCall(`/companies/${id}`, { method: "DELETE" });
export const getCompanyDashboard = (cid) => apiCall(`/companies/${cid}/dashboard`);

// Mappings
export const getProjectCompanyMappings = () => apiCall("/mappings/project-company");
export const updateProjectCompanyMapping = (pid, cid) => apiCall("/mappings/project-company", { method: "PUT", body: JSON.stringify({ project_id: pid, company_id: cid }) });

// Verticals
export const getVerticals = () => apiCall("/verticals");
export const createVertical = (data) => apiCall("/verticals", { method: "POST", body: JSON.stringify(data) });
export const updateVertical = (id, data) => apiCall(`/verticals/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteVertical = (id) => apiCall(`/verticals/${id}`, { method: "DELETE" });
export const updateProjectVertical = (pid, vid) => apiCall("/mappings/project-vertical", { method: "PUT", body: JSON.stringify({ project_id: pid, vertical_id: vid }) });

// Means of Finance (Input → Capex Means of Finance)
export const getMeansOfFinance = () => apiCall("/means-of-finance");
export const addMeansOfFinanceRow = (data) => apiCall("/means-of-finance", { method: "POST", body: JSON.stringify(data) });
export const updateMeansOfFinanceRow = (id, data) => apiCall(`/means-of-finance/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteMeansOfFinanceRow = (id) => apiCall(`/means-of-finance/${id}`, { method: "DELETE" });

// Group Organigram (free-form canvas: { nodes, edges })
export const getOrganigram = () => apiCall("/organigram");
export const addOrganigramNode = (data) => apiCall("/organigram", { method: "POST", body: JSON.stringify(data) });
export const updateOrganigramNode = (id, data) => apiCall(`/organigram/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteOrganigramNode = (id) => apiCall(`/organigram/${id}`, { method: "DELETE" });
export const setOrganigramPosition = (id, pos_x, pos_y) =>
  apiCall(`/organigram/${id}/position`, { method: "PUT", body: JSON.stringify({ pos_x, pos_y }) });
export const addOrganigramEdge = (data) =>
  apiCall("/organigram/edges", { method: "POST", body: JSON.stringify(data) });
export const updateOrganigramEdge = (id, data) =>
  apiCall(`/organigram/edges/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteOrganigramEdge = (id) =>
  apiCall(`/organigram/edges/${id}`, { method: "DELETE" });
export const syncOrganigramFromMapping = () =>
  apiCall("/organigram/sync-from-mapping", { method: "POST" });
export const loadOrganigramSample = () =>
  apiCall("/organigram/load-sample", { method: "POST" });

// Reports — Key Projection + Project Finance with shared filter params.
const _qs = (filters = {}) => {
  const parts = Object.entries(filters)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  return parts.length ? "?" + parts.join("&") : "";
};
export const getKeyProjection = (filters) => apiCall(`/reports/key-projection${_qs(filters)}`);
export const getProjectFinance = (filters) => apiCall(`/reports/project-finance${_qs(filters)}`);
export const getCashflow = (filters) => apiCall(`/reports/cashflow${_qs(filters)}`);
export const getPnl = (filters) => apiCall(`/reports/pnl${_qs(filters)}`);

// Budget Summary (Input → Budget Summary)
export const getBudgetSummary = () => apiCall("/budget-summary");
export const updateBudgetSummaryCell = (data) => apiCall("/budget-summary", { method: "PUT", body: JSON.stringify(data) });
export const addBudgetSummaryRow = (data) => apiCall("/budget-summary/rows", { method: "POST", body: JSON.stringify(data) });
export const deleteBudgetSummaryRow = (data) => apiCall("/budget-summary/rows", { method: "DELETE", body: JSON.stringify(data) });
export const renameBudgetSummaryRow = (data) => apiCall("/budget-summary/rows/rename", { method: "PUT", body: JSON.stringify(data) });

// Variable Templates
export const getVariableTemplates = () => apiCall("/variable-templates");
export const addVariableTemplate = (data) => apiCall("/variable-templates", { method: "POST", body: JSON.stringify(data) });
export const deleteVariableTemplate = (id) => apiCall(`/variable-templates/${id}`, { method: "DELETE" });
export const updateVariableTemplate = (id, data) => apiCall(`/variable-templates/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const reorderVariableTemplates = (items) => apiCall(`/variable-templates/reorder`, { method: "PUT", body: JSON.stringify({ items }) });
export const getAffectedProjects = (projectType) => apiCall(`/variable-templates/affected-projects?project_type=${projectType}`);

// Variable Categories (user-managed, ordered)
export const getVariableCategories = () => apiCall("/variable-categories");
export const addVariableCategory = (data) => apiCall("/variable-categories", { method: "POST", body: JSON.stringify(data) });
export const updateVariableCategory = (id, data) => apiCall(`/variable-categories/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteVariableCategory = (id) => apiCall(`/variable-categories/${id}`, { method: "DELETE" });
export const reorderVariableCategories = (items) => apiCall(`/variable-categories/reorder`, { method: "PUT", body: JSON.stringify({ items }) });

// Notifications
export const getNotifications = () => apiCall("/notifications");
export const markNotificationRead = (id) => apiCall(`/notifications/${id}/read`, { method: "POST" });
export const markAllNotificationsRead = () => apiCall("/notifications/read-all", { method: "POST" });

// Users
export const getUsers = () => apiCall("/users");
export const createUser = (data) => apiCall("/users", { method: "POST", body: JSON.stringify(data) });
export const updateUser = (id, data) => apiCall(`/users/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteUser = (id) => apiCall(`/users/${id}`, { method: "DELETE" });

// Settings
export const getSettings = () => apiCall("/settings");
export const updateSettings = (data) => apiCall("/settings", { method: "PUT", body: JSON.stringify(data) });

// Formulas (variable projection)
export const getFormulas = () => apiCall("/formulas");
export const createFormula = (data) => apiCall("/formulas", { method: "POST", body: JSON.stringify(data) });
export const updateFormula = (id, data) => apiCall(`/formulas/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteFormula = (id) => apiCall(`/formulas/${id}`, { method: "DELETE" });

// Statement Line Formulas (P&L / Cash Flow / Loan computation)
export const getStatementFormulas = () => apiCall("/statement-formulas");
export const createStatementFormula = (data) => apiCall("/statement-formulas", { method: "POST", body: JSON.stringify(data) });
export const updateStatementFormula = (id, data) => apiCall(`/statement-formulas/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteStatementFormula = (id) => apiCall(`/statement-formulas/${id}`, { method: "DELETE" });
export const reorderStatementFormulas = (order) => apiCall("/statement-formulas/reorder", { method: "PUT", body: JSON.stringify({ order }) });

// Project Assumptions + Sub-units
export const getProjectAssumptions = (pid) => apiCall(`/projects/${pid}/assumptions`);
export const updateProjectAssumptions = (pid, assumptions) => apiCall(`/projects/${pid}/assumptions`, { method: "PUT", body: JSON.stringify({ assumptions }) });

// Assumption Keys (global registry — Input Data row catalog)
export const getAssumptionKeys = () => apiCall("/assumption-keys");
export const addAssumptionKey = (data) => apiCall("/assumption-keys", { method: "POST", body: JSON.stringify(data) });
export const updateAssumptionKey = (id, data) => apiCall(`/assumption-keys/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteAssumptionKey = (id) => apiCall(`/assumption-keys/${id}`, { method: "DELETE" });
export const reorderAssumptionKeys = (items) => apiCall("/assumption-keys/reorder", { method: "PUT", body: JSON.stringify({ items }) });
export const addProjectSubUnit = (pid, data) => apiCall(`/projects/${pid}/sub-units`, { method: "POST", body: JSON.stringify(data) });
export const updateProjectSubUnit = (pid, sid, data) => apiCall(`/projects/${pid}/sub-units/${sid}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteProjectSubUnit = (pid, sid) => apiCall(`/projects/${pid}/sub-units/${sid}`, { method: "DELETE" });

// Capex (Input → Capex Year-wise)
export const getCapex = () => apiCall("/capex");
export const addCapexRow = (data) => apiCall("/capex", { method: "POST", body: JSON.stringify(data) });
export const updateCapexRow = (id, data) => apiCall(`/capex/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteCapexRow = (id) => apiCall(`/capex/${id}`, { method: "DELETE" });
export const addCapexHistoricalYear = (year) =>
  apiCall("/capex/historical-year", { method: "POST", body: JSON.stringify({ year }) });
export const removeCapexHistoricalYear = (year) =>
  apiCall(`/capex/historical-year/${year}`, { method: "DELETE" });

// Project Line Items (Income / Expense breakdown)
export const getProjectLineItems = (pid) => apiCall(`/projects/${pid}/line-items`);
export const addProjectLineItem = (pid, data) => apiCall(`/projects/${pid}/line-items`, { method: "POST", body: JSON.stringify(data) });
export const updateProjectLineItem = (pid, lid, data) => apiCall(`/projects/${pid}/line-items/${lid}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteProjectLineItem = (pid, lid) => apiCall(`/projects/${pid}/line-items/${lid}`, { method: "DELETE" });

// History / Audit Trail
export const getGlobalHistory = (params = {}) => {
  // Accept either a number (legacy: limit) or a filter object.
  const filters = typeof params === "number" ? { limit: params } : { ...params };
  if (filters.limit == null) filters.limit = 200;
  const qs = Object.entries(filters)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
  return apiCall(`/history${qs ? "?" + qs : ""}`);
};
export const getHistoryFacets = () => apiCall("/history/facets");
export const getHistoryRetention = () => apiCall("/history/retention");
export const setHistoryRetention = (months) => apiCall("/history/retention", { method: "PUT", body: JSON.stringify({ months }) });
export const purgeHistory = (olderThanMonths) => apiCall("/history/purge", { method: "POST", body: JSON.stringify({ older_than_months: olderThanMonths }) });
export const getProjectHistory = (pid, params = {}) => {
  // Accept either a number (legacy: limit) or a filter object.
  const filters = typeof params === "number" ? { limit: params } : { ...params };
  if (filters.limit == null) filters.limit = 200;
  const qs = Object.entries(filters)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
  return apiCall(`/history/project/${pid}${qs ? "?" + qs : ""}`);
};
export const getProjectHistoryFacets = (pid) => apiCall(`/history/project/${pid}/facets`);
export const getVariableHistory = (vid, limit = 100) => apiCall(`/history/variable/${vid}?limit=${limit}`);
export const getVariableYearVersions = (vid, year) => apiCall(`/history/variable/${vid}/year/${year}`);